import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { trackAffiliateSignupComplete } from '../utils/analytics';
import './AffiliateRegistration.css';

const AFFILIATE_TERMS = `
Terms and Conditions for FreshFitLab Affiliate Program

1. Program Overview
By participating in the FreshFitLab Affiliate Program ("Program"), you ("Affiliate") agree to be bound by these terms and conditions. FreshFitLab reserves the right to modify these terms at any time.

2. Eligibility
- Must be at least 18 years old
- Must have a valid email account
- Must comply with all applicable laws and regulations

3. Commission Structure
- Base commission rate starts at 2% of qualifying sales
- Commissions are calculated on the total sale amount before taxes and fees
- Commission rates may be adjusted based on performance
- Minimum payout threshold of $50

4. Payment Terms
- Payments are processed monthly for the previous month's earnings
- Payments are made via PayPal or approved payment method
- Affiliate is responsible for all applicable taxes
- Unclaimed payments expire after 12 months

5. Affiliate Obligations
- Accurately represent FreshFitLab's products and services
- Disclose affiliate relationship in compliance with FTC guidelines
- Maintain current and accurate contact and payment information
- Not engage in spam or deceptive marketing practices
- Not bid on FreshFitLab trademarks in paid advertising

6. Prohibited Activities
- Spamming or unsolicited email marketing
- False or misleading advertising
- Unauthorized use of FreshFitLab's intellectual property
- Creation of fake or fraudulent orders
- Manipulation of tracking systems or commission calculations

7. Content Guidelines
- All content must be original or properly licensed
- Content must not be offensive, illegal, or inappropriate
- FreshFitLab reserves the right to request content modifications
- Affiliate maintains ownership of their original content

8. Usage Limits
- Menu uploads are limited to 100 menus per affiliate account
- AI image generation is limited to 20 per day
- These limits may be adjusted based on account standing

9. Termination
- FreshFitLab reserves the right to suspend or terminate any affiliate relationship at any time
- Immediate suspension for violation of terms
- Final payment of earned commissions upon termination

10. Liability
- Affiliate operates as an independent contractor
- FreshFitLab is not liable for affiliate's business operations
- Affiliate agrees to indemnify FreshFitLab against claims

11. Privacy and Data
- Affiliate must comply with privacy laws and GDPR
- Maintain confidentiality of customer data
- Use tracking data only for Program purposes

12. Governing Law
These terms are governed by the laws of the jurisdiction where FreshFitLab is registered.

By accepting these terms, you acknowledge that you have read, understood, and agree to be bound by all the conditions outlined above.`;

const AffiliateRegistration = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    specialization: 'nutrition',
    businessName: '',
    businessType: '',
    website: '',
    socialMedia: {
      instagram: '',
      facebook: '',
      twitter: '',
      tiktok: ''
    },
    paymentInfo: {
      email: '',
      type: 'PAYPAL'
    },
    acceptedTerms: false
  });
  const [loading, setLoading] = useState(false);
  const [checkingStatus, setCheckingStatus] = useState(true);
  const [error, setError] = useState(null);
  const [registrationStatus, setRegistrationStatus] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [showTerms, setShowTerms] = useState(false);
  const [showOptionalFields, setShowOptionalFields] = useState(false);
  const [roleState, setRoleState] = useState({ nutritionEnabled: false, fitnessEnabled: false });
  const [roleLoading, setRoleLoading] = useState(false);

  useEffect(() => {
    checkRegistrationStatus();
  }, []);

  const checkRegistrationStatus = async () => {
    try {
      setCheckingStatus(true);
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_API_URL}/affiliate/register`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        if (data.affiliate) {
          setRegistrationStatus(data.affiliate.status);
          setRoleState({
            nutritionEnabled: data.affiliate.nutritionEnabled ?? (data.affiliate.specialization !== 'fitness'),
            fitnessEnabled: data.affiliate.fitnessEnabled ?? (data.affiliate.specialization !== 'nutrition')
          });
          setFormData({
            businessName: data.affiliate.businessName || '',
            businessType: data.affiliate.businessType || '',
            website: data.affiliate.website || '',
            socialMedia: data.affiliate.socialMedia || {
              instagram: '',
              facebook: '',
              twitter: '',
              tiktok: ''
            },
            paymentInfo: data.affiliate.paymentInfo || {
              email: '',
              type: 'PAYPAL'
            },
            acceptedTerms: true
          });
        }
      }
    } catch (err) {
      console.error('Error checking registration status:', err);
    } finally {
      setCheckingStatus(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (type === 'checkbox') {
      setFormData(prev => ({
        ...prev,
        [name]: checked
      }));
    } else if (name.includes('.')) {
      const [parent, child] = name.split('.');
      setFormData(prev => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: value
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.acceptedTerms) {
      setError('You must accept the terms and conditions to register');
      return;
    }

    setLoading(true);
    setError(null);
    setSuccessMessage(null);

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_API_URL}/affiliate/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(formData)
      });

      const responseText = await response.text();
      
      if (!response.ok) {
        let errorMessage = 'Registration failed';
        try {
          const errorData = JSON.parse(responseText);
          errorMessage = errorData.error || errorMessage;
        } catch (parseError) {
          errorMessage = `Server error: ${response.status} ${response.statusText}`;
        }
        throw new Error(errorMessage);
      }

      const data = JSON.parse(responseText);
      setRegistrationStatus(data.affiliate.status);
      setSuccessMessage(data.message || 'Welcome to the affiliate program!');

      trackAffiliateSignupComplete(formData.specialization);

      // Update localStorage with new role data
      const storedUser = JSON.parse(localStorage.getItem('user') || '{}');
      storedUser.isAffiliate = true;
      storedUser.specialization = formData.specialization;
      storedUser.nutritionEnabled = formData.specialization === 'nutrition' || formData.specialization === 'both';
      storedUser.fitnessEnabled = formData.specialization === 'fitness' || formData.specialization === 'both';
      localStorage.setItem('user', JSON.stringify(storedUser));

      // Redirect to the appropriate dashboard
      const dashboard = formData.specialization === 'fitness' ? '/coach/dashboard' : '/affiliate/dashboard';
      setTimeout(() => {
        navigate(dashboard);
      }, 2000);
    } catch (err) {
      setError(err.message || 'An unexpected error occurred during registration');
    } finally {
      setLoading(false);
    }
  };

  const handleRoleToggle = async (role) => {
    const newState = { ...roleState, [role]: !roleState[role] };
    
    // Don't allow disabling both
    if (!newState.nutritionEnabled && !newState.fitnessEnabled) {
      setError('At least one role must be enabled');
      return;
    }

    setRoleLoading(true);
    setError(null);
    setSuccessMessage(null);

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_API_URL}/affiliate/roles`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(newState)
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to update roles');
      }

      const data = await response.json();
      setRoleState({
        nutritionEnabled: data.nutritionEnabled,
        fitnessEnabled: data.fitnessEnabled
      });

      // Update localStorage user data so navbar updates immediately
      const storedUser = JSON.parse(localStorage.getItem('user') || '{}');
      storedUser.specialization = data.specialization;
      storedUser.nutritionEnabled = data.nutritionEnabled;
      storedUser.fitnessEnabled = data.fitnessEnabled;
      localStorage.setItem('user', JSON.stringify(storedUser));

      setSuccessMessage('Roles updated successfully!');
    } catch (err) {
      setError(err.message);
    } finally {
      setRoleLoading(false);
    }
  };

  if (checkingStatus) {
    return (
      <div className="affiliate-registration">
        <div className="loading-spinner">
          <i className="fas fa-spinner fa-spin"></i>
          <p>Checking registration status...</p>
        </div>
      </div>
    );
  }

  // If already registered, show role management
  if (registrationStatus) {
    return (
      <div className="affiliate-registration">
        <div className="registration-hero">
          <div className="hero-icon">
            <i className="fas fa-user-check"></i>
          </div>
          <h1>Your Creator / Coach Roles</h1>
          <p className="registration-intro">
            Manage which roles are active. Disabling a role hides your public page but keeps your data safe.
          </p>
        </div>

        {error && <div className="error-message"><i className="fas fa-exclamation-circle"></i> {error}</div>}
        {successMessage && <div className="success-message"><i className="fas fa-check-circle"></i> {successMessage}</div>}

        {registrationStatus === 'SUSPENDED' && (
          <div className="suspended-notice" style={{ margin: '0 auto 1.5rem', maxWidth: '600px' }}>
            <i className="fas fa-exclamation-triangle"></i>
            <p>Your account has been suspended. Please contact support.</p>
          </div>
        )}

        {registrationStatus === 'ACTIVE' && (
          <div className="role-management">
            <div className="specialization-options">
              <div 
                className={`specialization-card ${roleState.nutritionEnabled ? 'selected' : ''}`}
                onClick={() => !roleLoading && handleRoleToggle('nutritionEnabled')}
                style={{ cursor: roleLoading ? 'wait' : 'pointer' }}
              >
                <div className="role-status-badge" style={{ 
                  background: roleState.nutritionEnabled ? '#10b981' : '#6b7280',
                  color: 'white',
                  padding: '2px 10px',
                  borderRadius: '12px',
                  fontSize: '0.75rem',
                  fontWeight: 600,
                  position: 'absolute',
                  top: '10px',
                  right: '10px'
                }}>
                  {roleState.nutritionEnabled ? 'Active' : 'Inactive'}
                </div>
                <i className="fas fa-utensils"></i>
                <span className="specialization-title">Nutrition Creator</span>
                <span className="specialization-desc">Publish recipes & meal plans</span>
                {roleState.nutritionEnabled && (
                  <button
                    className="dashboard-button"
                    style={{ marginTop: '0.75rem', fontSize: '0.85rem', padding: '0.4rem 1rem' }}
                    onClick={(e) => { e.stopPropagation(); navigate('/affiliate/dashboard'); }}
                  >
                    <i className="fas fa-tachometer-alt"></i> Creator Dashboard
                  </button>
                )}
              </div>
              <div 
                className={`specialization-card ${roleState.fitnessEnabled ? 'selected' : ''}`}
                onClick={() => !roleLoading && handleRoleToggle('fitnessEnabled')}
                style={{ cursor: roleLoading ? 'wait' : 'pointer' }}
              >
                <div className="role-status-badge" style={{ 
                  background: roleState.fitnessEnabled ? '#10b981' : '#6b7280',
                  color: 'white',
                  padding: '2px 10px',
                  borderRadius: '12px',
                  fontSize: '0.75rem',
                  fontWeight: 600,
                  position: 'absolute',
                  top: '10px',
                  right: '10px'
                }}>
                  {roleState.fitnessEnabled ? 'Active' : 'Inactive'}
                </div>
                <i className="fas fa-dumbbell"></i>
                <span className="specialization-title">Fitness Coach</span>
                <span className="specialization-desc">Create workouts & train clients</span>
                {roleState.fitnessEnabled && (
                  <button
                    className="dashboard-button"
                    style={{ marginTop: '0.75rem', fontSize: '0.85rem', padding: '0.4rem 1rem' }}
                    onClick={(e) => { e.stopPropagation(); navigate('/coach/dashboard'); }}
                  >
                    <i className="fas fa-tachometer-alt"></i> Coach Dashboard
                  </button>
                )}
              </div>
            </div>
            <p className="role-hint" style={{ textAlign: 'center', color: '#6b7280', marginTop: '1rem', fontSize: '0.9rem' }}>
              Click a card to enable or disable that role
            </p>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="affiliate-registration">
      <div className="registration-hero">
        <div className="hero-icon">
          <i className="fas fa-handshake"></i>
        </div>
        <h1>Become a Creator / Coach</h1>
        <p className="registration-intro">
          Join our creator program in seconds! Share recipes, build workout programs, and grow your audience.
        </p>
      </div>

      {error && <div className="error-message"><i className="fas fa-exclamation-circle"></i> {error}</div>}
      {successMessage && <div className="success-message"><i className="fas fa-check-circle"></i> {successMessage}</div>}

      <form onSubmit={handleSubmit} className="registration-form" noValidate>
        {/* Specialization selection */}
        <div className="form-section specialization-section">
          <h2><i className="fas fa-user-tag"></i> What do you specialize in?</h2>
          <div className="specialization-options">
            <label className={`specialization-card ${formData.specialization === 'nutrition' ? 'selected' : ''}`}>
              <input
                type="radio"
                name="specialization"
                value="nutrition"
                checked={formData.specialization === 'nutrition'}
                onChange={handleInputChange}
              />
              <i className="fas fa-utensils"></i>
              <span className="specialization-title">Nutrition Creator</span>
              <span className="specialization-desc">Share recipes & meal plans</span>
            </label>
            <label className={`specialization-card ${formData.specialization === 'fitness' ? 'selected' : ''}`}>
              <input
                type="radio"
                name="specialization"
                value="fitness"
                checked={formData.specialization === 'fitness'}
                onChange={handleInputChange}
              />
              <i className="fas fa-dumbbell"></i>
              <span className="specialization-title">Fitness Coach</span>
              <span className="specialization-desc">Create workouts & programs</span>
            </label>
            <label className={`specialization-card ${formData.specialization === 'both' ? 'selected' : ''}`}>
              <input
                type="radio"
                name="specialization"
                value="both"
                checked={formData.specialization === 'both'}
                onChange={handleInputChange}
              />
              <i className="fas fa-star"></i>
              <span className="specialization-title">Both</span>
              <span className="specialization-desc">Nutrition + fitness coaching</span>
            </label>
          </div>
        </div>

        {/* Quick signup section */}
        <div className="quick-signup-section">
          <div className="benefits-list">
            <div className="benefit-item">
              <i className="fas fa-check"></i>
              <span>{formData.specialization === 'fitness' ? 'Create custom workouts & programs' : 'Create custom meal plans'}</span>
            </div>
            <div className="benefit-item">
              <i className="fas fa-check"></i>
              <span>{formData.specialization === 'fitness' ? 'Assign programs to clients' : 'AI-powered assistance'}</span>
            </div>
            <div className="benefit-item">
              <i className="fas fa-check"></i>
              <span>{formData.specialization === 'fitness' ? 'Client payment collection via Stripe' : 'Earn commissions on sales'}</span>
            </div>
            <div className="benefit-item">
              <i className="fas fa-check"></i>
              <span>{formData.specialization === 'fitness' ? 'Public coach profile page' : 'Import menus from images or URLs'}</span>
            </div>
            <div className="benefit-item">
              <i className="fas fa-check"></i>
              <span>{formData.specialization === 'fitness' ? 'Client scheduling & progress tracking' : 'Custom nutrition plans for clients'}</span>
            </div>
            <div className="benefit-item upgrade-hint">
              <i className="fas fa-crown"></i>
              <span>Upgrade to <a href="/pricing">Pro</a> for higher limits</span>
            </div>
          </div>
        </div>

        {/* Terms section */}
        <div className="form-section terms-section">
          <div className="terms-container">
            <button
              type="button"
              className="view-terms-button"
              onClick={() => setShowTerms(!showTerms)}
            >
              <i className={`fas fa-${showTerms ? 'chevron-up' : 'chevron-down'}`}></i>
              {showTerms ? 'Hide Terms' : 'View Terms and Conditions'}
            </button>
            
            {showTerms && (
              <div className="terms-content">
                <pre>{AFFILIATE_TERMS}</pre>
              </div>
            )}
            
            <div className="terms-acceptance">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  name="acceptedTerms"
                  checked={formData.acceptedTerms}
                  onChange={handleInputChange}
                  required
                />
                <span className="checkmark"></span>
                I have read and agree to the Terms and Conditions
              </label>
            </div>
          </div>
        </div>

        {/* Optional fields toggle */}
        <div className="optional-fields-toggle">
          <button
            type="button"
            className="toggle-optional-button"
            onClick={() => setShowOptionalFields(!showOptionalFields)}
          >
            <i className={`fas fa-${showOptionalFields ? 'minus' : 'plus'}`}></i>
            {showOptionalFields ? 'Hide Optional Info' : 'Add Business Info (Optional)'}
          </button>
          <p className="optional-hint">You can add this information later from your dashboard</p>
        </div>

        {/* Optional fields */}
        {showOptionalFields && (
          <>
            <div className="form-section">
              <h2><i className="fas fa-building"></i> Business Information</h2>
              
              <div className="form-group">
                <label htmlFor="businessName">Business Name</label>
                <input
                  type="text"
                  id="businessName"
                  name="businessName"
                  value={formData.businessName}
                  onChange={handleInputChange}
                  placeholder="Your business or brand name"
                />
              </div>

              <div className="form-group">
                <label htmlFor="businessType">Business Type</label>
                <select
                  id="businessType"
                  name="businessType"
                  value={formData.businessType}
                  onChange={handleInputChange}
                >
                  <option value="">Select a type...</option>
                  <option value="BLOG">Blog</option>
                  <option value="SOCIAL_MEDIA">Social Media</option>
                  <option value="WEBSITE">Website</option>
                  <option value="OTHER">Other</option>
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="website">Website</label>
                <input
                  type="url"
                  id="website"
                  name="website"
                  value={formData.website}
                  onChange={handleInputChange}
                  placeholder="https://your-website.com"
                />
              </div>
            </div>

            <div className="form-section">
              <h2><i className="fas fa-share-alt"></i> Social Media</h2>
              
              <div className="social-grid">
                <div className="form-group">
                  <label htmlFor="instagram"><i className="fab fa-instagram"></i> Instagram</label>
                  <input
                    type="text"
                    id="instagram"
                    name="socialMedia.instagram"
                    value={formData.socialMedia.instagram}
                    onChange={handleInputChange}
                    placeholder="@username"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="tiktok"><i className="fab fa-tiktok"></i> TikTok</label>
                  <input
                    type="text"
                    id="tiktok"
                    name="socialMedia.tiktok"
                    value={formData.socialMedia.tiktok}
                    onChange={handleInputChange}
                    placeholder="@username"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="facebook"><i className="fab fa-facebook"></i> Facebook</label>
                  <input
                    type="text"
                    id="facebook"
                    name="socialMedia.facebook"
                    value={formData.socialMedia.facebook}
                    onChange={handleInputChange}
                    placeholder="Page name or URL"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="twitter"><i className="fab fa-twitter"></i> Twitter/X</label>
                  <input
                    type="text"
                    id="twitter"
                    name="socialMedia.twitter"
                    value={formData.socialMedia.twitter}
                    onChange={handleInputChange}
                    placeholder="@username"
                  />
                </div>
              </div>
            </div>

            <div className="form-section">
              <h2><i className="fas fa-credit-card"></i> Payment Information</h2>
              <p className="section-hint">Required to receive commission payments</p>
              
              <div className="form-group">
                <label htmlFor="paymentEmail">PayPal Email</label>
                <input
                  type="email"
                  id="paymentEmail"
                  name="paymentInfo.email"
                  value={formData.paymentInfo.email}
                  onChange={handleInputChange}
                  placeholder="your-paypal@email.com"
                />
              </div>
            </div>
          </>
        )}

        <div className="form-actions">
          <button 
            type="submit" 
            className="submit-button" 
            disabled={loading || !formData.acceptedTerms}
          >
            {loading ? (
              <>
                <i className="fas fa-spinner fa-spin"></i> Creating Account...
              </>
            ) : (
              <>
                <i className="fas fa-rocket"></i> Start as Creator/Coach
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default AffiliateRegistration;
